import { useQuery } from '@tanstack/react-query'
import React, { useState } from 'react'
import { UnsavedChangesModal } from '../../../common/components/UnsavedChangesModal'
import { BackofficePermissionsEnum as Permissions } from '../../../common/enums/backOfficePermissionsEnum'
import usePermissions from '../../../common/hooks/usePermissions'
import { useUnsavedChanges } from '../../../common/hooks/useUnsavedChanges'
import { customSwal } from '../../../config/swal'
import { FundService, IFundRiskProfileDraftItem, IRiskProfile } from '../../../services/FundService'
import { DocumentsSidebar } from './components/recommended/DocumentsSidebar'
import { ProfileSelect } from './components/recommended/ProfileSelect'
import { RecommendedFundsSkeleton } from './components/recommended/RecommendedFundsSkeleton'

export const RecommendedFundsConfig: React.FC = () => {
  const { hasPermission } = usePermissions()
  const [dirtyProfiles, setDirtyProfiles] = useState<Record<string, number>>({})
  const [fundAction, setFundAction] = useState<'saving' | 'publishing' | null>(null)

  const isDirty = Object.keys(dirtyProfiles).length > 0

  const { data: riskProfiles = [], isLoading: isLoadingProfiles } = useQuery<IRiskProfile[]>({
    queryKey: ['risk-profiles'],
    queryFn: () => FundService().getRiskProfiles(),
  })

  const { data: fundDrafts = [], isLoading: isLoadingFunds, refetch: refetchFunds } = useQuery<IFundRiskProfileDraftItem[]>({
    queryKey: ['fund-risk-profile-draft'],
    queryFn: () => FundService().getFundRiskProfileDraft(),
  })

  const getProfileId = (fund: IFundRiskProfileDraftItem): number | null =>
    dirtyProfiles[fund.cod_fund] ?? fund.risk_profile_id ?? null

  const updateProfile = (codFund: string, profileId: number) =>
    setDirtyProfiles((prev) => ({ ...prev, [codFund]: profileId }))

  const saveDirtyProfiles = async () => {
    const items = Object.entries(dirtyProfiles).map(([cod_fund, risk_profile_id]) => ({
      cod_fund,
      risk_profile_id,
    }))
    await FundService().saveFundRiskProfileDraft({ items })
    setDirtyProfiles({})
  }

  const handleSave = async () => {
    setFundAction('saving')
    try {
      await saveDirtyProfiles()
      refetchFunds()
      customSwal.fire({ title: 'Cambios guardados correctamente', icon: 'success' })
    } catch {
      customSwal.fire({ title: 'Error al guardar los cambios', icon: 'error' })
    } finally {
      setFundAction(null)
    }
  }

  const handlePublish = async () => {
    setFundAction('publishing')
    try {
      await saveDirtyProfiles()
      await FundService().publishFundRiskProfileDraft()
      refetchFunds()
      customSwal.fire({ title: 'Publicado correctamente', icon: 'success' })
    } catch {
      customSwal.fire({ title: 'Error al publicar', icon: 'error' })
    } finally {
      setFundAction(null)
    }
  }

  const { isModalOpen, handleLeave, handleCancel, handleSaveAndContinue, saving } =
    useUnsavedChanges(isDirty, handleSave, { blockRouter: true })

  if (isLoadingFunds || isLoadingProfiles) return <RecommendedFundsSkeleton />

  return (
    <>
      <div className='card p-8'>
        {/* ── Estado ── */}
        <div className='d-flex align-items-center gap-3 mb-8'>
          <span
            style={{
              background: '#d4edda',
              color: '#155724',
              fontSize: 13,
              padding: '3px 12px',
              borderRadius: 20,
              fontWeight: 600,
            }}
          >
            Edición activo
          </span>
        </div>

        {/* ── Documentos + sidebar ── */}
        <DocumentsSidebar />

        {/* ── Tabla de fondos ── */}
        {hasPermission(Permissions.RECOMMENDED_CONFIG.SHOW) && (
          <div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                padding: '8px 16px',
                marginBottom: 4,
              }}
            >
              <span className='fw-bold fs-7 text-gray-500 text-uppercase'>Fondo</span>
              <span className='fw-bold fs-7 text-gray-500 text-uppercase'>Perfiles de tolerancia</span>
            </div>

            {fundDrafts.map((fund, idx) => (
              <div key={fund.cod_fund}>
                {idx > 0 && <hr style={{ margin: 0, borderColor: '#F3F4F6' }} />}
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr',
                    alignItems: 'center',
                    padding: '16px',
                  }}
                >
                  <span className='fw-semibold fs-6 text-gray-700'>{fund.title}</span>
                  <ProfileSelect
                    value={getProfileId(fund)}
                    profiles={riskProfiles}
                    onChange={(profileId) => updateProfile(fund.cod_fund, profileId)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Acciones ── */}
        {hasPermission([
          Permissions.RECOMMENDED_CONFIG.SAVE,
          Permissions.RECOMMENDED_CONFIG.PUBLISH,
        ]) && (
          <div className='d-flex justify-content-end gap-3 mt-8'>
            {hasPermission(Permissions.RECOMMENDED_CONFIG.SAVE) && (
              <button
                type='button'
                className='btn btn-custom-action'
                onClick={handleSave}
                disabled={fundAction !== null}
              >
                {fundAction === 'saving' ? (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    Guardando...
                    <span className='spinner-border spinner-border-sm align-middle ms-2' />
                  </span>
                ) : (
                  'Guardar cambios'
                )}
              </button>
            )}
            {hasPermission(Permissions.RECOMMENDED_CONFIG.PUBLISH) && (
              <button
                type='button'
                className='btn btn-custom-action'
                onClick={handlePublish}
                disabled={fundAction !== null}
              >
                {fundAction === 'publishing' ? (
                  <span className='indicator-progress' style={{ display: 'block' }}>
                    Publicando...
                    <span className='spinner-border spinner-border-sm align-middle ms-2' />
                  </span>
                ) : (
                  'Publicar'
                )}
              </button>
            )}
          </div>
        )}
      </div>

      {isModalOpen && (
        <UnsavedChangesModal
          onLeave={handleLeave}
          onCancel={handleCancel}
          onSave={handleSaveAndContinue}
          saving={saving}
        />
      )}
    </>
  )
}
